import React, { useState, useEffect, useRef } from "react";
import { ApiGet } from "../../helpers/API/ApiData";
import { useDebounce } from "../../hooks";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

interface User {
  _id: string;
  firstName: string;
  lastName?: string;
  emailId?: string;
  userProfile?: string[];
  status?: {
    name: string;
  };
}

interface Props {
  onSelectUser: (userId: string, user: User) => void;
  disabled?: boolean;
}

const UserAutocomplete: React.FC<Props> = ({ onSelectUser, disabled = false }) => {
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noResults, setNoResults] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebounce(search, 500);

  // Fetch users when search term changes
  useEffect(() => {
    const fetchUsers = async () => {
      if (!debouncedSearch || debouncedSearch.length < 1) {
        setUsers([]);
        setShowDropdown(false);
        setNoResults(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      setNoResults(false);

      try {
        // Use the correct route: /user/find-for-admin
        const response: any = await ApiGet(
          `/user/find-for-admin?letter=${encodeURIComponent(debouncedSearch)}&limit=20`
        );

        if (response?.data?.success && Array.isArray(response?.data?.data)) {
          // Filter to only active users
          const activeUsers = response.data.data.filter(
            (user: User) => user.status?.name === "active"
          );
          setUsers(activeUsers);
          setShowDropdown(activeUsers.length > 0);
          setNoResults(activeUsers.length === 0);
        } else {
          setUsers([]);
          setShowDropdown(false);
          setNoResults(true);
        }
      } catch (err: any) {
        console.error("Error fetching users:", err);
        setError("Benutzersuche fehlgeschlagen");
        setUsers([]);
        setShowDropdown(false);
        setNoResults(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [debouncedSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectUser = (user: User) => {
    onSelectUser(user._id, user);
    setSearch("");
    setUsers([]);
    setShowDropdown(false);
  };

  const handleClear = () => {
    setSearch("");
    setUsers([]);
    setShowDropdown(false);
  };

  const getUserDisplayName = (user: User) => {
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    return `${firstName} ${lastName}`.trim() || "Unbekannter Benutzer";
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div
        style={{
          background: "rgba(186, 186, 186, 0.1)",
          border: "0.5px solid #bababa",
          borderRadius: "4px",
          padding: "6px 10px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <SearchIcon style={{ color: "#bababa", fontSize: "16px", flexShrink: 0 }} />
        <input
          type="text"
          placeholder="Benutzer suchen..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => users.length > 0 && setShowDropdown(true)}
          disabled={disabled}
          style={{
            width: "100%",
            border: "none",
            background: "transparent",
            color: "#343434",
            fontSize: "12px",
            fontWeight: 400,
            outline: "none",
          }}
        />
        {isLoading ? (
          <span
            style={{
              width: "12px",
              height: "12px",
              border: "2px solid #bababa",
              borderTopColor: "#00AEEF",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              flexShrink: 0,
            }}
          />
        ) : search ? (
          <CloseIcon
            style={{ fontSize: "14px", cursor: "pointer", color: "#bababa", flexShrink: 0 }}
            onClick={handleClear}
          />
        ) : null}
      </div>

      {/* Dropdown */}
      {showDropdown && users.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#ffffff",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            borderRadius: "4px",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 1000,
            marginTop: "4px",
          }}
        >
          {users.map((user) => (
            <div
              key={user._id}
              onClick={() => handleSelectUser(user)}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                borderBottom: "1px solid #f0f0f0",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLDivElement).style.background = "rgba(0, 174, 239, 0.1)";
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLDivElement).style.background = "transparent";
              }}
            >
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  overflow: "hidden",
                  background: "#f0f0f0",
                  flexShrink: 0,
                }}
              >
                {user.userProfile?.[0] ? (
                  <img
                    src={user.userProfile[0]}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      color: "#bababa",
                    }}
                  >
                    {(user.firstName?.trim() || user.lastName?.trim())?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#343434",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {getUserDisplayName(user)}
                </div>
                {user.emailId && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#606060",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {user.emailId}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results message */}
      {noResults && !isLoading && debouncedSearch && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#ffffff",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            borderRadius: "4px",
            padding: "12px",
            zIndex: 1000,
            marginTop: "4px",
            textAlign: "center",
            color: "#606060",
            fontSize: "12px",
          }}
        >
          Keine aktiven Benutzer gefunden
        </div>
      )}

      {/* Error message */}
      {error && (
        <div
          style={{
            color: "#c62828",
            fontSize: "11px",
            marginTop: "4px",
          }}
        >
          {error}
        </div>
      )}

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default UserAutocomplete;
